import { VopOutcome, VopResult } from 'src/app/api/models';

/**
 * How a Verification of Payee (VoP) result reads to a handler. VoP is the
 * bank's check of an IBAN against the payee's name; it is independent of
 * the handler's "ownership confirmed" mark (docs/contexts/payments/GLOSSARY.md).
 */
export const VOP_OUTCOME_LABELS: Record<VopOutcome, string> = {
  [VopOutcome.Match]: 'Match',
  [VopOutcome.CloseMatch]: 'Close match',
  [VopOutcome.NoMatch]: 'No match',
  [VopOutcome.NotPossible]: 'Not possible',
  [VopOutcome.Rejected]: 'Rejected',
};

/**
 * The result's label, "(not valid)" when the server says it no longer
 * stands for its pair (`valid`): expired, or a rejected request or a
 * technical not possible, which are never reused.
 */
export function vopResultLabel(result: VopResult): string {
  const label = VOP_OUTCOME_LABELS[result.outcome] ?? result.outcome;
  return result.valid ? label : `${label} (not valid)`;
}

/** The holder's name as the bank knows it, shown for a close match only. */
export function vopHolderName(result: VopResult): string | null {
  return result.outcome === VopOutcome.CloseMatch && result.holder_name ? result.holder_name : null;
}

/** The outcomes the invoice page flags beside the IBAN: the bank says the name is not, or not quite, the account holder's. */
const FLAGGED: ReadonlySet<VopOutcome> = new Set([VopOutcome.NoMatch, VopOutcome.CloseMatch]);

/**
 * The result the invoice page flags beside its IBAN: a no match or a close
 * match, valid or expired. A match, any other outcome and a missing result
 * flag nothing.
 */
export function flaggedVopResult(result: VopResult | null | undefined): VopResult | null {
  return result && FLAGGED.has(result.outcome) ? result : null;
}
